import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useDimensionValuesByGrouping } from '~/hooks/use-dimension-values';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	groupingId: number | null;
	groupingName: string;
}

function Swatch({ hex }: { hex?: string | null }) {
	if (!hex) return <span className="text-foreground-disabled">—</span>;
	return (
		<span className="inline-flex items-center gap-1.5">
			<span className="h-4 w-4 rounded-sm border border-border" style={{ backgroundColor: hex }} />
			<span className="font-mono text-[11px] text-foreground-muted">{hex}</span>
		</span>
	);
}

/** Read-only side panel listing the dimension values that belong to a grouping (legacy read-only modal). */
export function GroupingValuesSheet({ open, onOpenChange, groupingId, groupingName }: Props) {
	const { t } = useTranslation();
	const { data, isLoading, isError } = useDimensionValuesByGrouping(open ? groupingId : null);
	const rows = [...(data ?? [])].sort((a, b) => (a.si ?? 0) - (b.si ?? 0));

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
				<SheetHeader className="border-b border-border px-5 py-4">
					<SheetTitle>{t('attributes.dimensionValues.title')}</SheetTitle>
					<SheetDescription>{groupingName}</SheetDescription>
				</SheetHeader>

				<div className="px-5 py-3 text-xs text-foreground-muted">{t('attributes.dimensionValues.count', { count: rows.length })}</div>

				<div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
					{isLoading ? (
						<Skeleton className="h-64 w-full rounded-md" />
					) : isError || rows.length === 0 ? (
						<div className="py-12 text-center text-sm text-foreground-subtle">{t('common.noResults')}</div>
					) : (
						<div className="overflow-hidden rounded-md border border-border">
							<table className="w-full border-collapse text-left text-sm">
								<thead>
									<tr className="border-b border-border bg-elevated text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle">
										<th className="px-3 py-2">{t('attributes.dimensionValues.columns.value')}</th>
										<th className="px-3 py-2">{t('attributes.dimensionValues.columns.color')}</th>
										<th className="px-3 py-2">{t('attributes.dimensionValues.columns.code')}</th>
									</tr>
								</thead>
								<tbody>
									{rows.map(v => (
										<tr key={v.id} className="border-b border-border-subtle last:border-b-0">
											<td className="px-3 py-2 font-medium text-foreground">{v.vtxt || '—'}</td>
											<td className="px-3 py-2">
												<Swatch hex={v.chex} />
											</td>
											<td className="px-3 py-2 font-mono text-xs text-foreground-muted">{v.cd || '—'}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</SheetContent>
		</Sheet>
	);
}
